import { ClientError } from '@ricoh/ria.client.core/errors';

import type { DataType, DataTypeGeneral, DataTypeList, DataTypeTable } from '~/common/model/api/common';

import { DATA_TYPE_GENERAL_NAMES } from '~/common/constants/feature/settings/batch-type-library';
import commonLogic from '~/common/logic/common';

import type { DataTypeFormField, DataTypeFormFieldGeneral, DataTypeFormFieldList } from './_type.type';

import { isDataTypeFormField, TYPE_FORM_FIELD_DEFAULT } from './_utils';

/**
 * Create General DataType or List DataType FormField.
 *
 * @param {DataType} fieldType FieldType.
 *
 * @returns {DataTypeFormFieldGeneral | DataTypeFormFieldList | undefined} General DataType or List DataType FormField.
 */
function createGeneralOrListDataTypeFormField(fieldType: DataType): DataTypeFormFieldGeneral | DataTypeFormFieldList | undefined {
  if (DATA_TYPE_GENERAL_NAMES.includes(fieldType.name)) {
    const generalType = fieldType as DataTypeGeneral;

    return {
      type: {
        menuKey: generalType.name,
        value: generalType.name,
      },
      format: generalType.format,
    };
  }

  const listFieldType = commonLogic.type.getListDataType(fieldType.name);
  if (listFieldType) {
    const listType = fieldType as DataTypeList;

    return {
      type: {
        menuKey: 'list',
        value: listFieldType,
      },
      items: listType.items ?? [],
      format: listType.format,
    };
  }

  return undefined;
}

/**
 * Create DataType FormField.
 *
 * @param {DataType} fieldType FieldType.
 *
 * @returns {DataTypeFormField | undefined} Type FormField.
 */
export function createDataTypeFormField(fieldType: DataType): DataTypeFormField {
  const result = createGeneralOrListDataTypeFormField(fieldType);
  if (result) {
    return result;
  }

  if (fieldType.name === 'table') {
    return {
      type: {
        menuKey: 'table',
        value: 'table',
      },
      columns:
        fieldType.columns?.map(({ id, name, type }) => {
          const dataType = createGeneralOrListDataTypeFormField(type) ?? TYPE_FORM_FIELD_DEFAULT;

          return {
            id,
            name,
            ...dataType,
          };
        }) ?? [],
    };
  }

  return TYPE_FORM_FIELD_DEFAULT;
}

/**
 * Create General FieldType or List FieldType from DataType FormField.
 *
 * @param {DataTypeFormField} dataTypeFormField DataType FormField.
 *
 * @returns {DataTypeGeneral | DataTypeList | undefined} General FieldType or List FieldType.
 */
function createGeneralOrListFieldType(dataTypeFormField: DataTypeFormField): DataTypeGeneral | DataTypeList | undefined {
  const format = 'format' in dataTypeFormField ? dataTypeFormField.format : undefined;

  if (isDataTypeFormField.list(dataTypeFormField)) {
    return {
      name: `list.${dataTypeFormField.type.value}`,
      items: 'items' in dataTypeFormField ? dataTypeFormField.items : [],
      format,
    } as DataTypeList;
  }

  if (isDataTypeFormField.general(dataTypeFormField)) {
    return {
      name: dataTypeFormField.type.value,
      format,
    } as DataTypeGeneral;
  }

  return undefined;
}

/**
 * Create DataType from DataType FormField.
 *
 * @param {DataTypeFormField} dataTypeFormField DataType FormField.
 *
 * @returns {DataType} DataType.
 */
export function createDataType(dataTypeFormField: DataTypeFormField): DataType {
  const result = createGeneralOrListFieldType(dataTypeFormField);
  if (result) {
    return result;
  }

  if (isDataTypeFormField.table(dataTypeFormField)) {
    return {
      name: 'table',
      columns:
        'columns' in dataTypeFormField
          ? dataTypeFormField.columns.map((column) => {
              const tableTypeColumn = createGeneralOrListFieldType(column);
              if (!tableTypeColumn) {
                throw new ClientError({
                  module: 'logic',
                  message: 'Invalid column data type',
                  cause: column,
                });
              }

              return {
                id: column.id,
                name: column.name,
                type: tableTypeColumn,
              };
            })
          : [],
    } as DataTypeTable;
  }

  throw new ClientError({
    module: 'logic',
    message: 'Invalid data type',
    cause: dataTypeFormField,
  });
}
